import { apiListSensors } from 'apis/SensorApi';
import { apiAdminChangeUserPassword, apiCreateUser, apiDeleteUser, apiListUsersSortedByDisplayOrder, apiUpdateUser, apiUpdateUsersDisplayOrder } from 'apis/UserApi';
import { apiCreateUserDeviceGroups, apiGetUserDeviceGroups, apiUpdateUserDeviceGroups } from 'apis/UserDeviceGroups';
import DetailHeader from 'components/DetailHeader';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { CURRENT_PAGE_DEFAULT } from 'constants/config';
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE, DIALOG_ERROR_INVALID_CONFIRM_PASSWORD, DIALOG_ERROR_USE_SHORT_KEYWORD, DIALOG_MESSAGE_CHANGED_PASSWORD_SUCCESSFULLY, DIALOG_MESSAGE_CONFIRM_DELETE_USER, DIALOG_MESSAGE_CREATED_UDER_SUCCESSFULLY, DIALOG_MESSAGE_DELETED_UDER_SUCCESSFULLY, DIALOG_MESSAGE_UPDATED_UDER_SUCCESSFULLY, TEXT_CREATE_USER, TEXT_UPDATE_USER, TEXT_USER_MANAGEMENT } from 'constants/message';
import { useAuth } from 'context/AuthContext';
import { useDialog } from 'context/DialogContext';
import { AccountType } from 'models';
import ChangePasswordForm from 'pages/Profile/components/ChangePasswordForm';
import ModifyUserForm from 'pages/UserManagement/components/ModifyUserForm';
import DragDropUserList from 'pages/UserManagement/components/DragDropUserList';
import React, { useEffect, useCallback, useRef } from 'react';
import { standardizeString } from 'utils/function';
import ManagedDeviceList from 'components/ManagedDeviceList';
import UserListHeader from './components/UserListHeader';
import "./UserManagementPage.css";

const propTypes = {};
const defaultProps = {};
const UserManagementPage = (props) => {
  /*** States and Variables ***/
  const { currentUser, setCurrentUser } = useAuth();
  const [curPage, setCurPage] = React.useState(CURRENT_PAGE_DEFAULT);
  const [loading, setLoading] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [sensors, setSensors] = React.useState([]);
  const [keyword, setKeyword] = React.useState('');
  const [searchedUsers, setSearchedUsers] = React.useState(null);
  const { error, success, warningConfirm } = useDialog();
  const [modifyingUser, setModifyingUser] = React.useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);
  const saveOrderTimeoutRef = useRef(null);
  /*** Processing ***/
  const initUsers = React.useCallback(async () => {
    // Use the sorted API to get users in display order
    const result = await apiListUsersSortedByDisplayOrder();
    if (result.success) {
      if (currentUser?.accountType === AccountType.ADMIN) {
        setUsers(result.data);
      }
      else if (currentUser?.accountType === AccountType.MANAGER) {
        setUsers(result.data.filter(user => user.accountType !== AccountType.ADMIN));
      }
    }
    else {
      error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
    }
  }, [currentUser, error]);

  const initSensors = React.useCallback(async () => {
    const sensorsResult = await apiListSensors();
    if (sensorsResult.success && sensorsResult.data && sensorsResult.data.items) {
      const sensorsData = sensorsResult.data.items;
      const sortedSensors = [...sensorsData].sort((a, b) => {
        const idA = a.managementId ? (isNaN(Number(a.managementId)) ? a.managementId : Number(a.managementId)) : '';
        const idB = b.managementId ? (isNaN(Number(b.managementId)) ? b.managementId : Number(b.managementId)) : '';
        return idA > idB ? 1 : idA < idB ? -1 : 0;
      });
      setSensors(sortedSensors);
    } else {
      error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
    }
  }, [error]);

  useEffect(() => {
    initUsers();
    initSensors();
  }, [initUsers, initSensors]);

  // Cleanup effect to clear the timeout when component unmounts
  useEffect(() => {
    return () => {
      if (saveOrderTimeoutRef.current) {
        clearTimeout(saveOrderTimeoutRef.current);
      }
    };
  }, []);

  /**
   * call this function after creating, modifying, deleting an user
   */
  const validateSearchedUsers = (users) => {
    if (searchedUsers) {
      let keywords = standardizeString(keyword).replaceAll(/[、､ 　]/g, ",");
      keywords = keywords.split(",");
      keywords = keywords.map(keyword => keyword.trim());
      keywords = keywords.filter(keyword => keyword !== "");
      setSearchedUsers(searchUsers(users, keywords));
    }
  }

  const searchUsers = (users, keywords) => {
    const searchResult = [];
    for (const user of users) {
      const userInfo = standardizeString(user.name) + ' ' +
        standardizeString(user.email) + ' ' +
        standardizeString(user.contact) + ' ' +
        standardizeString(user.phone) + ' ' +
        standardizeString(user.description) + ' ' +
        standardizeString(user.createdAt.split(' ')[0]);
      let accepted = true;
      for (const keyword of keywords) {
        if (!userInfo.includes(keyword)) {
          accepted = false;
          break;
        }
      }
      if (accepted) searchResult.push(user);
    }
    return searchResult;
  }

  /*** Sub Components ***/
  const renderUserList = () => {
    return <>
      <div className='user_management_page_list_container'>
        <DragDropUserList
          currentUser={currentUser}
          items={searchedUsers || users}
          curPage={curPage}
          onEditItem={setModifyingUser}
          onDeleteItem={onDeleteUser}
          onPageChange={setCurPage}
          setSelectedUser={setSelectedUser}
          selectedUser={selectedUser}
          onReorder={handleUserReorder}
          dragDisabled={searchedUsers !== null || isSavingOrder} // Disable drag when searching or saving order
        />
        {isSavingOrder && <FanLoadingIcon strokeWidth={6} />}
      </div>
      <div>
        <ManagedDeviceList currentUser={selectedUser} type="USER" />
      </div>
    </>
  }

  const renderUserModifyingForm = () => {
    if (changePasswordOpen) {
      return <ChangePasswordForm
        isManagementForm={true}
        onCancel={() => { setChangePasswordOpen(false) }}
        onSubmit={onChangePassword} />
    }
    return <ModifyUserForm
      user={modifyingUser}
      sensors={sensors}
      onCancel={() => { setModifyingUser(null) }}
      onModifyUser={onModifyUser}
      onChangePassword={() => { setChangePasswordOpen(true) }} />;
  }
  /*** Event Handlers ***/
  // Debounced function to save user order to backend
  const saveUserOrderToBackend = useCallback(async (reorderedUsers) => {
    try {
      setIsSavingOrder(true);

      // Create the order data with display order indices
      const userOrderData = reorderedUsers.map((user, index) => ({
        id: user.id,
        displayOrder: index + 1 // Start from 1 instead of 0
      }));

      // Update the display order in the backend
      const result = await apiUpdateUsersDisplayOrder(userOrderData);

      if (result.success) {
        success('ユーザーの順序が正常に更新されました');
        // Refresh the full user list to get the updated data
        await initUsers();
      } else {
        error('ユーザーの順序更新に失敗しました');
        // Revert the UI changes on error
        await initUsers();
      }
    } catch (err) {
      error('ユーザーの順序更新中にエラーが発生しました');
      // Revert the UI changes on error
      await initUsers();
    } finally {
      setIsSavingOrder(false);
    }
  }, [success, error, initUsers]);

  const handleUserReorder = useCallback((reorderedUsers) => {
    // Update the users state with the new order immediately for better UX
    setUsers(reorderedUsers);

    // Clear any existing timeout
    if (saveOrderTimeoutRef.current) {
      clearTimeout(saveOrderTimeoutRef.current);
    }

    // Debounce the backend save operation (save after 500ms of no more changes)
    saveOrderTimeoutRef.current = setTimeout(() => {
      saveUserOrderToBackend(reorderedUsers);
    }, 500);
  }, [saveUserOrderToBackend]);

  const onDeleteUser = (user) => {
    if (loading) return;
    warningConfirm(
      DIALOG_MESSAGE_CONFIRM_DELETE_USER,
      async () => {
        setLoading(true);
        const result = await apiDeleteUser(user.id, user.email);
        setLoading(false);
        if (result.success) {
          success(DIALOG_MESSAGE_DELETED_UDER_SUCCESSFULLY);

          // Refresh the users list to get the updated display orders
          // since the backend compacts the orders after deletion
          await initUsers();

          // Update selected user if the deleted user was selected
          if (selectedUser.id === result.data.id) {
            setSelectedUser({
              id: currentUser.id,
              email: currentUser.email,
              nameList: currentUser?.data?.nameList || [],
              labelArea: currentUser?.data?.labelArea || ""
            });
          }
        }
        else {
          error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
        }
      },
      {}
    )
  }
  const onSearch = (originalKeyword, keywords) => {
    setKeyword(originalKeyword);
    if (!keywords.length) {
      setSearchedUsers(null);
      return;
    }
    setSearchedUsers(searchUsers(users, keywords));
    setCurPage(1);
  }
  const onModifyUser = async (userData, deviceDirections = []) => {
    if (loading) return;
    if (userData.password !== userData.confirmPassword) {
      error(DIALOG_ERROR_INVALID_CONFIRM_PASSWORD);
      return;
    }
    else if (userData.password && userData.password.length < 8) {
      error(DIALOG_ERROR_USE_SHORT_KEYWORD);
      return;
    }
    setLoading(true);
    let userId
    if (!modifyingUser.id) {
      const createResult = await apiCreateUser(userData);
      if (createResult.success) {
        success(DIALOG_MESSAGE_CREATED_UDER_SUCCESSFULLY);

        // Refresh the users list to get the updated display orders
        // since the backend assigns display order to new users
        await initUsers();

        userId = createResult.data.id;
      }
      else {
        return error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      }
    }
    else {
      const updateResult = await apiUpdateUser({
        id: modifyingUser.id,
        name: userData.name,
        email: userData.email,
        accountType: userData.accountType,
        contact: userData.contact,
        phone: userData.phone,
        enabledMFA: userData.enabledMFA,
        description: userData.description,
        data: userData.data,
      });
      if (updateResult.success) {
        success(DIALOG_MESSAGE_UPDATED_UDER_SUCCESSFULLY);
        users.splice(users.indexOf(modifyingUser), 1, updateResult.data);
        validateSearchedUsers(users);
        setUsers(Object.assign([], users));
        userId = updateResult.data.id;
        if (currentUser.id === userId) {
          setCurrentUser(updateResult.data);
        }
      }
      else {
        return error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      }
    }

    // Handle user device groups
    if (userId) {
      const newNameList = userData.data?.nameList || [];
      // Fetch the user's device groups to check if they exist
      const userDeviceGroupsResult = await apiGetUserDeviceGroups(userId);
      const userDeviceGroups = userDeviceGroupsResult.success ? userDeviceGroupsResult.data : null;

      if (!userDeviceGroups) {
        // Case 1: User doesn't have device groups yet, create them
        const defaultGroups = {
          userId,
          group1: {
            name: "Group 1",
            devices: deviceDirections
          },
          group2: { name: "Group 2", devices: [] },
          group3: { name: "Group 3", devices: [] },
          group4: { name: "Group 4", devices: [] },
          group5: { name: "Group 5", devices: [] }
        };

        await apiCreateUserDeviceGroups(defaultGroups);
      } else {
        // Case 2: User already has device groups, update them
        // First, get the current nameList to determine what changed
        const initialNameList = modifyingUser.data?.nameList || [];

        // Find items that were added or removed
        const removedItemIds = initialNameList.filter(item => !newNameList.includes(item));
        const addedItems = deviceDirections.filter(item => !initialNameList.includes(item.id));

        if (removedItemIds.length > 0 || addedItems.length > 0) {
          const updatedGroups = { ...userDeviceGroups };

          // Remove items from all groups
          if (removedItemIds.length > 0) {
            for (let i = 1; i <= 5; i++) {
              const groupKey = `group${i}`;
              if (updatedGroups[groupKey]?.devices) {
                updatedGroups[groupKey].devices = updatedGroups[groupKey].devices.filter(
                  device => !removedItemIds.includes(device.id)
                );
              }
            }
          }

          // Add new items to group1
          if (addedItems.length > 0 && updatedGroups.group1) {
            updatedGroups.group1.devices = [
              ...(updatedGroups.group1.devices || []),
              ...addedItems
            ];
          }

          // Update the device groups
          await apiUpdateUserDeviceGroups({
            userId,
            ...updatedGroups
          });

          // Update selectedUser's nameList
          if (selectedUser && selectedUser.id === userId) {
            setSelectedUser(prev => ({
              ...prev,
              nameList: newNameList
            }));
          }
        }
      }
    }
    setModifyingUser(null);
    setLoading(false);
  }
  const onChangePassword = async (_, newPassword) => {
    if (loading) return;
    setLoading(true);
    const result = await apiAdminChangeUserPassword(modifyingUser.email, newPassword);
    if (result.success) {
      success(DIALOG_MESSAGE_CHANGED_PASSWORD_SUCCESSFULLY);
      setChangePasswordOpen(false);
    }
    else {
      error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
    }
    setLoading(false);
  }
  /*** Main Render ***/
  return <div className='user_management_page_container'>
    {
      modifyingUser ?
        <DetailHeader
          mainLabel={TEXT_USER_MANAGEMENT}
          subLabel={modifyingUser.id ? TEXT_UPDATE_USER : TEXT_CREATE_USER}
          onClick={() => { setModifyingUser(null) }}
        /> :
        <UserListHeader
          setModifyingUser={setModifyingUser}
          keyword={keyword}
          onSearch={onSearch}
        />
    }

    {/* {renderPath()} */}
    {modifyingUser ?
      renderUserModifyingForm() :
      renderUserList()}
    {loading && <FanLoadingIcon size={80} />}
  </div>;
}
UserManagementPage.propTypes = propTypes;
UserManagementPage.defaultProps = defaultProps;
export default UserManagementPage;